import { getConfig, setConfig } from '../../configs/config';
import messages from '../../constants/logMessages.json';
import tracking from '../../tracking/tracking';
import {
  AnalyticsEventTypes,
  AnalyticsPageView,
  IndicativeQuote,
  Purchase,
  QuoteComplete,
  TransactionComplete,
} from '../../types';
import logging, { getMessage } from '../logging/index';
import tools from '../tools/index';

const { warnHeading, warnBold } = messages;

export type CompletionEvents =
  | IndicativeQuote
  | QuoteComplete
  | Purchase
  | TransactionComplete;

/**
 * This function handles the setting of analytics start time based on various data points.
 * The timestamps set here will later be used to calculate the duration of journeys ending with complete events.
 *
 * @param {AnalyticsPageView} payload - The analytics payload containing information about the page view.
 * @returns {false | undefined} - Returns false if no matching case is found or if there's an error; otherwise, returns undefined.
 */
export const startTimeHandler = (
  payload: AnalyticsPageView,
  mfeName?: string
): false | undefined => {
  if (!payload.data) return false;

  const {
    transactionStartView = false,
    quoteStartView = false,
    buyStartView = false,
    paymentStartView = false,
    appStart = false,
    transactionType = '',
    ...filteredData
  } = payload.data;

  const analyticsTime = tools.handlePersistedConfig?.()?.analyticsTime || {};

  const {
    quoteStartViewTime = 0,
    buyStartViewTime = 0,
    paymentStartViewTime = 0,
    appStartTime = 0,
  } = analyticsTime ?? {};

  const trackViewEvent = (event: string, cmMetric?: string) => {
    const viewEventData: unknown = { ...filteredData };
    if (cmMetric) {
      (viewEventData as Record<string, unknown>)[cmMetric] = 1; // Type assertion
    }

    // Add Transaction Type into start_view event
    if (transactionType) {
      (viewEventData as Record<string, unknown>)['transactionType'] =
        transactionType; // Type assertion
    }

    const viewEvent: Record<string, unknown> = {
      event,
      data: viewEventData,
    };
    if (mfeName) {
      viewEvent['mfeName'] = mfeName;
    }

    tracking.analyticsCustomEvent(viewEvent);
  };

  // Check if multiple flags can be set and set them accordingly

  // Register AppStart Time
  if (appStart && appStartTime === 0) {
    setConfig({
      analyticsTime: {
        ...tools.handlePersistedConfig()?.analyticsTime,
        appStartTime: tools.getNow(),
      },
    });
  }
  // Check if transactionStartView exists
  if (transactionStartView) {
    // Sanitise transactionType for consistency
    const sanitisedTransactionType = transactionType
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (match: string, index: number) =>
        index === 0 ? match.toLowerCase() : match.toUpperCase()
      )
      .replace(/\s+/g, ''); // Remove spaces

    // Check if transactionType is not set
    if (!transactionType) {
      // Warn if transactionStartViewTime is not set
      logging.warn(
        getMessage('transactionStartViewTimeNotSet', [
          sanitisedTransactionType,
        ]),
        warnHeading,
        warnBold
      );
      return false;
    }

    // Track view event for transactionStartView
    trackViewEvent(
      AnalyticsEventTypes.TransactionStartView,
      'cm_transaction_start_view'
    );

    // Construct the key for transaction start view time

    const transactionStartViewTimeKey = `${sanitisedTransactionType}TransactionStartViewTime`;

    // Check if the key does not exist in the analyticsTime configuration
    if (analyticsTime && !analyticsTime?.[transactionStartViewTimeKey]) {
      // Set the transaction start view time if it doesn't exist
      setConfig({
        analyticsTime: {
          ...getConfig().analyticsTime,
          [transactionStartViewTimeKey]: tools.getNow(),
        },
      });
    }
  }

  // quoteStartView
  if (quoteStartView) {
    trackViewEvent(AnalyticsEventTypes.QuoteStartView, 'cm_quote_start_view');
    if (quoteStartViewTime === 0) {
      setConfig({
        analyticsTime: {
          ...tools.handlePersistedConfig()?.analyticsTime,
          quoteStartViewTime: tools.getNow(),
        },
      });
    }
  }

  // buyStartView
  if (buyStartView) {
    trackViewEvent(AnalyticsEventTypes.BuyStartView, 'cm_buy_start_view');
    if (buyStartViewTime === 0) {
      setConfig({
        analyticsTime: {
          ...tools.handlePersistedConfig()?.analyticsTime,
          buyStartViewTime: tools.getNow(),
        },
      });
    }
  }

  // paymentStartView
  if (paymentStartView) {
    trackViewEvent(AnalyticsEventTypes.PaymentStartView, 'paymentStartView');
    if (paymentStartViewTime === 0) {
      setConfig({
        analyticsTime: {
          ...tools.handlePersistedConfig?.()?.analyticsTime,
          paymentStartViewTime: tools.getNow?.(),
        },
      });
    }
  }

  return false;
};

export const completeTimeHandler = (payload: CompletionEvents) => {
  const { event = '', data = {} } = payload;
  const { transactionType = '', appComplete, timeAppCompletion } = data;
  const updatedData: Record<string, any> = {};
  const { enableMilestonesPersist } = getConfig();
  const updatedPayload = (updatedData: object) => ({
    ...payload,
    data: {
      ...data,
      ...updatedData,
    },
  });

  // Transaction Complete
  if (event === AnalyticsEventTypes.TransactionComplete) {
    const getConfigValue = (key: string) =>
      getConfig()?.analyticsTime?.[key] ?? null;

    const sanitisedTransactionType = transactionType.replace(
      /[^a-zA-Z0-9]+(.)/g,
      (_: string, group1: string) => group1.toUpperCase()
    );
    const capitalizedTransactionType =
      sanitisedTransactionType.charAt(0).toUpperCase() +
      sanitisedTransactionType.slice(1);

    const transactionStartViewTime = getConfigValue(
      `${sanitisedTransactionType}TransactionStartViewTime`
    );
    const appStartTime = getConfigValue('appStartTime');

    if (transactionStartViewTime || appComplete) {
      if (transactionStartViewTime) {
        const transactionCompleteTime = tools.getTimeElapsedInSeconds?.(
          transactionStartViewTime
        );
        updatedData[`time${capitalizedTransactionType}`] =
          transactionCompleteTime;
        // Persist the Transaction Start View Time when `enableMilestonesPersist` is NOT ON.
        if (!enableMilestonesPersist)
          tools.resetTransactionStartView?.(transactionType);
      } else {
        logging.warn(
          getMessage('transactionStartViewTimeNotFound', [
            capitalizedTransactionType,
          ]),
          warnHeading,
          warnBold
        );
      }

      // Time App Completion
      if (appComplete && appStartTime) {
        const timeAppCompletion = tools.getTimeElapsedInSeconds?.(appStartTime);
        updatedData['timeAppCompletion'] = timeAppCompletion;
      } else if (appComplete) {
        logging.warn(getMessage('appStartTimeNotFound'), warnHeading, warnBold);
      }

      return updatedPayload(updatedData);
    }

    // No TransactionType
    if (!transactionType) {
      logging.warn(
        getMessage('transactionStartViewTimeNotSet', [
          capitalizedTransactionType,
        ]),
        warnHeading,
        warnBold
      );
    } else {
      logging.warn(
        getMessage('transactionStartViewTimeNotFound', [
          capitalizedTransactionType,
        ]),
        warnHeading,
        warnBold
      );
    }
  }

  // Indicative Quote
  if (event === AnalyticsEventTypes.IndicativeQuote) {
    const startTime =
      tools.handlePersistedConfig()?.analyticsTime?.quoteStartViewTime ?? null;
    if (startTime) {
      updatedData['timeIndicativeQuote'] =
        tools.getTimeElapsedInSeconds?.(startTime);

      return updatedPayload(updatedData);
    }
    logging.warn(
      getMessage('quoteStartViewTimeNotFound'),
      warnHeading,
      warnBold
    );
  }

  // Quote Complete
  if (event === AnalyticsEventTypes.QuoteComplete) {
    const startTime =
      tools.handlePersistedConfig()?.analyticsTime?.quoteStartViewTime ?? null;
    if (startTime) {
      updatedData['timeQuote'] = tools.getTimeElapsedInSeconds?.(startTime);
      return updatedPayload(updatedData);
    }
    logging.warn(
      getMessage('quoteStartViewTimeNotFound'),
      warnHeading,
      warnBold
    );
  }

  // Purchase
  if (event === AnalyticsEventTypes.Purchase) {
    const buyStartViewTime =
      tools.handlePersistedConfig()?.analyticsTime?.buyStartViewTime ?? null;
    const paymentStartViewTime =
      tools.handlePersistedConfig()?.analyticsTime?.paymentStartViewTime ??
      null;
    if (buyStartViewTime || appComplete || paymentStartViewTime) {
      // Time Purchase
      if (buyStartViewTime) {
        updatedData['timePurchase'] =
          tools.getTimeElapsedInSeconds?.(buyStartViewTime);
      } else {
        logging.warn(
          getMessage('buyStartViewTimeNotFound'),
          warnHeading,
          warnBold
        );
      }
      // Check if `timeAppCompletion` exists to avoid triggering it twice.
      if (appComplete && timeAppCompletion) {
        const appStartTime =
          tools.handlePersistedConfig()?.analyticsTime?.appStartTime ?? null;

        if (appStartTime) {
          updatedData['timeAppCompletion'] =
            tools.getTimeElapsedInSeconds?.(appStartTime);
        } else {
          logging.warn(
            getMessage('appStartTimeNotFound'),
            warnHeading,
            warnBold
          );
        }
      }
    }
    return updatedPayload(updatedData);
  }
  // Return the original payload if the condition is not met
  return payload;
};
